/** Auth gate and routes (docs/12-ux.md §3). */
import { useApi } from "./lib/data.ts";
import { match, RouterProvider, useLocation } from "./lib/router.tsx";
import type { User } from "./lib/types.ts";
import { Loading } from "./components/ui.tsx";
import { Login } from "./pages/Login.tsx";
import { Today } from "./pages/Today.tsx";
import { Needs } from "./pages/Needs.tsx";
import { Portfolio } from "./pages/Portfolio.tsx";
import { NewBot } from "./pages/NewBot.tsx";
import { BotPage } from "./pages/Bot.tsx";
import { Strategies } from "./pages/Strategies.tsx";
import { Builder } from "./pages/Builder.tsx";
import { NewWriter, Writer } from "./pages/Writer.tsx";
import { Backtests } from "./pages/Backtests.tsx";
import { BacktestPage } from "./pages/Backtest.tsx";
import { Trades } from "./pages/Trades.tsx";
import { Settings } from "./pages/Settings.tsx";
import { Chats } from "./pages/Chats.tsx";
import { Shell } from "./components/Shell.tsx";

function Routes({ user }: { user: User }) {
  const { path } = useLocation();
  let m: Record<string, string> | null;
  if (path === "/") return <Today user={user} />;
  if (path === "/needs") return <Needs />;
  if (path === "/bots") return <Portfolio />;
  if (path === "/bots/new") return <NewBot />;
  if ((m = match("/bots/:id/:tab?", path))) return <BotPage id={m.id!} tab={m.tab ?? "overview"} />;
  if (path === "/strategies") return <Strategies />;
  if (path === "/strategies/new") return <NewWriter />;
  if ((m = match("/writer/:id", path))) return <Writer key={m.id} id={m.id!} />;
  if ((m = match("/strategies/new/manual", path)) || (m = match("/strategies/:id/edit", path))) return <Builder id={m.id ?? null} />;
  if (path === "/backtests") return <Backtests />;
  if ((m = match("/backtests/:id/:tab?", path))) return <BacktestPage id={m.id!} tab={m.tab ?? "results"} />;
  if (path === "/trades") return <Trades />;
  if ((m = match("/chats/:bot?", path))) return <Chats botId={m.bot ?? null} />;
  if (path === "/settings") return <Settings user={user} />;
  return <Shell crumb="Not found"><div className="p-10 text-center text-muted">This page does not exist.</div></Shell>;
}

function Gate() {
  const { data, error, reload } = useApi<{ user: User }>("/me");
  if (error && (error as { status?: number }).status === 401) return <Login onDone={reload} />;
  if (error) return <div className="p-10 text-center text-muted">Can't reach the server: {error.message}</div>;
  if (!data) return <Loading />;
  return <Routes user={data.user} />;
}

export function App() {
  return <RouterProvider><Gate /></RouterProvider>;
}
